import { Component, useState, type ErrorInfo, type ReactNode } from 'react'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { RotateCcw, Send, TriangleAlert } from 'lucide-react'
import { db } from '@/lib/firebase'
import { Button, Card } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { COLLECTIONS } from '@shared/model'

/**
 * Fångar krascher innan de blir en vit skärm.
 *
 * Utan den här ser en förälder vars app kraschar i kassan ingenting alls, och
 * föreningen får aldrig veta att det hänt. Den enda signalen blir "det funkar
 * inte" nästa gång man ses, utan något att gå på.
 *
 * Rapporten skickas inte automatiskt. Den innehåller vilken sida man var på
 * och vad felet hette, och det är rimligt att den som drabbats får bestämma
 * om det ska lämna telefonen. Knappen finns direkt på skärmen, så tröskeln är
 * ett tryck – men det är ett tryck användaren gör.
 */

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  info: ErrorInfo | null
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info })
    // Loggas ändå i konsolen, så att felet går att hitta i en utvecklarsession
    // även om användaren väljer att inte skicka rapporten.
    console.error('Kraschade:', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children
    return <CrashScreen error={this.state.error} info={this.state.info} />
  }
}

function CrashScreen({ error, info }: { error: Error; info: ErrorInfo | null }) {
  const { user } = useAuth()
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState(false)

  async function send() {
    setSending(true)
    setFailed(false)
    try {
      /*
       * Meddelandet byggs av felet och var det hände. Komponentstacken
       * kapas – reglerna tar emot högst tvåtusen tecken, och det är de
       * första raderna som säger var det small.
       */
      const message = [
        `Appen kraschade på ${window.location.pathname}`,
        '',
        `${error.name}: ${error.message}`,
        '',
        (info?.componentStack ?? '').trim().split('\n').slice(0, 8).join('\n'),
      ]
        .join('\n')
        .slice(0, 2000)

      await addDoc(collection(db, COLLECTIONS.reports), {
        type: 'bug',
        message,
        status: 'new',
        userUid: user?.uid ?? null,
        userEmail: user?.email ?? null,
        page: window.location.pathname,
        userAgent: navigator.userAgent,
        createdAt: serverTimestamp(),
      })
      setSent(true)
    } catch {
      // Kraschen kan mycket väl bero på att uppkopplingen är borta, och då
      // går inte rapporten heller fram. Säg det rakt ut i stället för att
      // låta knappen se ut att ha fungerat.
      setFailed(true)
    } finally {
      setSending(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-lg items-center px-4 py-8">
      <Card className="w-full space-y-4 p-6 text-center">
        <TriangleAlert size={40} className="mx-auto text-warning" aria-hidden />

        <div>
          <h1 className="font-display text-xl font-extrabold">Något gick fel</h1>
          <p className="mt-1 text-sm text-muted">
            Appen hängde upp sig. Dina häften och rabatter är oförändrade – ingenting har gått
            förlorat.
          </p>
        </div>

        <Button
          fullWidth
          size="lg"
          icon={<RotateCcw size={18} />}
          onClick={() => window.location.reload()}
        >
          Ladda om appen
        </Button>

        {/*
         * Rapporten kräver inloggning.
         *
         * Reglerna tillåter bara den som är inloggad att skapa en rapport,
         * och den begränsningen är värd att behålla – en öppen skrivväg för
         * vem som helst är en öppen skrivväg. En utloggad besökare får därför
         * mejladressen i stället för en knapp som ändå skulle nekas.
         */}
        {!user ? (
          <p className="text-sm text-muted">
            Vill du berätta vad som hände? Mejla{' '}
            <a href="mailto:kansli@lirabk.se" className="font-semibold text-brand-ink underline">
              kansli@lirabk.se
            </a>
            .
          </p>
        ) : sent ? (
          <p className="text-sm font-semibold text-success">
            Tack! Rapporten är skickad till kansliet.
          </p>
        ) : (
          <>
            <Button
              variant="quiet"
              fullWidth
              loading={sending}
              icon={<Send size={16} />}
              onClick={() => void send()}
            >
              Skicka en rapport om felet
            </Button>
            <p className="text-xs text-muted">
              Rapporten innehåller vilken sida du var på och vad felet hette
              {user?.email ? `, samt din e-postadress så vi kan svara` : ''}. Inget skickas förrän
              du trycker.
            </p>
          </>
        )}

        {failed && (
          <p className="text-sm text-danger">
            Rapporten gick inte att skicka just nu – kanske är uppkopplingen borta. Ladda om och
            försök igen, eller mejla{' '}
            <a href="mailto:kansli@lirabk.se" className="underline">
              kansli@lirabk.se
            </a>
            .
          </p>
        )}
      </Card>
    </main>
  )
}
